"use client"

import React, { useState, useEffect, useMemo } from "react"
import { Drawer, Avatar, Typography, Tabs, Card, Spin, message, Button, Alert } from "antd"
import {
  ClockCircleOutlined,
  TrophyOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons"
import styled from "styled-components"
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"

// Import Redux actions and selectors
import {
  fetchActivityMap,
  setActivityDateRange,
  fetchPracticeScores,
  fetchCompletedPracticeAssessments,
  fetchStudentAssessments,
  fetchAssessmentScores,
  fetchLastAssessment,
  fetchLastPractice,
  fetchCEFRLevel,
  fetchScoreInfo,
  fetchStudentProgress
} from "../../actions"

import {
  selectActivityData,
  selectActivityLoading,
  selectActivityError,
  selectPracticeScores,
  selectPracticeScoresLoading,
  selectPracticeScoresError,
  selectStudentDataSent,
  selectStudentDataPending,
  selectStudentDataInProgress,
  selectStudentDataDroppedOff,
  selectStudentDataCompleted,
  selectStudentDataLoading,
  selectCommunicationAssessmentCount,
  selectAptitudeAssessmentCount,
  selectTechnicalAssessmentCount,
  selectCompletedPracticeAssessmentsLoading,
  selectTotalPracticeHours,
  selectFormattedTotalPracticeHours,
  selectStudentAssessments,
  selectStudentAssessmentsLoading,
  selectStudentAssessmentsError,
  selectAssessmentScores,
  selectAssessmentScoresLoading,
  selectAssessmentScoresError,
  selectLastAssessment,
  selectLastAssessmentLoading,
  selectLastAssessmentError,
  selectLastPractice,
  selectLastPracticeLoading,
  selectLastPracticeError,
  selectFormattedLastAssessment,
  selectFormattedLastPractice,
  selectCEFRLevel,
  selectCEFRLevelLoading,
  selectCEFRLevelError,
  selectCEFRScore,
  selectFormattedCEFRData,
  selectScoreInfo,
  selectScoreInfoLoading,
  selectScoreInfoError,
} from "../../selectors"

// Import Tab Components
import SummaryTab from "./Partials/SummaryTab"
import AssessmentTab from "./Partials/AssessmentTab"
import PracticeTab from "./Partials/PracticeTab"
import StudentReport from '../StudentReport'

const { Title, Text } = Typography
const { TabPane } = Tabs

// Styled Components with consistent design system
const StyledDrawer = styled(Drawer)`
  .ant-drawer-content {
    overflow: hidden;
  }
  .ant-drawer-body {
    padding: 0;
    overflow-y: auto;
  }
  .ant-drawer-header {
    border-bottom: 1px solid #f0f0f0;
    padding: 16px 24px;
    background: white;
  }
`
const HeaderSection = styled.div`
  padding: 24px;
  background: #f6f8fa;
  color: #262626;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #262626;
`

const DateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  font-size: 14px;
  color: #595959;
  
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 32px;
    justify-content: center;
  }
  
  > div {
    display: flex;
    align-items: center;
    font-weight: 500;
    
    .anticon {
      color: #8c8c8c;
    }
  }
`

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
    padding: 0 24px;
    background: white;
    position: sticky;
    top: 140px;
    z-index: 5;
  }
  
  .ant-tabs-tab {
    font-weight: 500;
    font-size: 14px;
    
    &.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: #3249D7;
      font-weight: 600;
    }
  }
`

// Activity Tracker styled components
const ActivityCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  background: white;
  margin-bottom: 24px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    background: #fafafa;
    border-radius: 8px 8px 0 0;
    padding: 12px 16px;
    min-height: auto;
  }

  .ant-card-head-title {
    font-size: 16px;
    font-weight: 600;
    color: #333333;
  }

  .ant-card-body {
    padding: 20px;
  }
`

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
  color: #333333;
  
  .nav-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: #f5f5f5;
    border: 1px solid #d9d9d9;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: #e6f7ff;
      border-color: #91d5ff;
      color: #3249D7;
    }
  }
  
  .month-label {
    margin: 0 16px;
    min-width: 140px;
    text-align: center;
    font-weight: 600;
  }
`

const ActivityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 16px;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
`

const WeekdaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
`

const WeekdayLabel = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  font-weight: 500;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ActivityDay = styled.div`
  aspect-ratio: 1;
  border-radius: 4px;
  border: 1px solid ${(p) => (p.active ? "#0FB680" : "#f0f0f0")};
  background: ${(p) => (p.active ? "#BFF4E0" : "#fafafa")};
  color: ${(p) => (p.active ? "#0FB680" : "#bfbfbf")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: ${(p) => (p.active ? "pointer" : "default")};
  min-height: 28px;

  &:hover {
    ${(p) =>
      p.active &&
      `
      background: #BFF4E0;
      border-color: #0FB680;
      transform: scale(1.05);
    `}
  }
`

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  font-size: 12px;
  color: #666666;
  
  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  
  .legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    border: 1px solid #0FB680;
    background: #BFF4E0;
  }
  
  .legend-trophy {
    color: #faad14;
    font-size: 12px;
  }
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  flex-direction: column;
  gap: 12px;
  
  .loading-text {
    color: #666666;
    font-size: 12px;
  }
`

const BackButton = styled(Button)`
  position: absolute;
  top: 16px;
  left: 16px;
  background: #ffffff;
  border: 1px solid #d9d9d9;
  color: #595959;
  font-weight: 500;
  border-radius: 6px;
  height: 32px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  &:hover, &:focus {
    background: #f0f8ff;
    border-color: #91d5ff;
    color: #1890ff;
    transform: translateX(-2px);
  }
`

// Mock Data (keeping the same)
const mockProgressData = [
  { day: 1, communication: 60, aptitude: 40, technical: 30 },
  { day: 5, communication: 65, aptitude: 45, technical: 35 },
  { day: 10, communication: 70, aptitude: 50, technical: 45 },
  { day: 15, communication: 75, aptitude: 55, technical: 50 },
  { day: 20, communication: 80, aptitude: 60, technical: 55 },
  { day: 25, communication: 85, aptitude: 65, technical: 60 },
  { day: 30, communication: 90, aptitude: 70, technical: 65 },
]

const suggestedRoles = [
  "Data Analyst",
  "Software Developer",
  "Marketing Specialist",
  "Project Manager",
  "Business Analyst",
]

// Custom hook for activity data formatting (keeping the same logic)
const useFormattedActivityData = () => {
  const activityData = useSelector(selectActivityData)
  const [currentDate, setCurrentDate] = useState(new Date())

  return React.useMemo(() => {
    const activeDatesMap = {}
    activityData.forEach((item) => {
      activeDatesMap[item.Date] = {
        isActive: true,
        hasAchievement: item.is_trophy,
        cefr: item.Cefr,
      }
    })

    const currentMonth = currentDate.getMonth()
    const currentYear = currentDate.getFullYear()
    const firstDay = new Date(currentYear, currentMonth, 1)
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()

    const formattedData = []
    for (let i = 0; i < 35; i++) {
      if (i < startingDayOfWeek) {
        formattedData.push({
          day: null,
          isActive: false,
          hasAchievement: false,
          cefr: null,
        })
      } else if (i - startingDayOfWeek + 1 <= daysInMonth) {
        const day = i - startingDayOfWeek + 1
        const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
        const activityInfo = activeDatesMap[dateStr] || {
          isActive: false,
          hasAchievement: false,
          cefr: null,
        }
        formattedData.push({
          day,
          ...activityInfo,
        })
      } else {
        formattedData.push({
          day: null,
          isActive: false,
          hasAchievement: false,
          cefr: null,
        })
      }
    }

    return { formattedData, setCurrentDate }
  }, [activityData, currentDate])
}

// Memoize the CustomTooltip component
const CustomTooltip = React.memo(({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const sessionData = payload[0].payload

    const hasPracticeData = sessionData["Practice Progress"] !== null
    const hasAssessmentData = sessionData["Communication Progress"] !== null

    let sessionTitle = `Session ${label}`
    if (hasPracticeData && hasAssessmentData) {
      sessionTitle = `Session ${label} (Practice & Assessment)`
    } else if (hasPracticeData) {
      sessionTitle = `Practice Session ${sessionData.originalPracticeSession || label}`
    } else if (hasAssessmentData) {
      sessionTitle = `Assessment Session ${sessionData.originalAssessmentSession || label}`
    }

    return (
      <div
        style={{
          backgroundColor: "white",
          border: "1px solid #d9d9d9",
          borderRadius: "6px",
          padding: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          fontSize: "12px",
        }}
      >
        <p
          style={{
            fontWeight: "600",
            margin: "0 0 8px 0",
            color: "#262626",
          }}
        >
          {sessionTitle}
        </p>
        {payload.map((entry, index) => {
          if (entry.value === null || entry.value === undefined) return null

          const icons = {
            Reading: "📖",
            Listening: "🎧",
            Speaking: "🗣️",
            "Practice Progress": "📊",
            "Communication Progress": "🎯",
          }
          return (
            <p key={index} style={{ margin: "4px 0", color: entry.color }}>
              <span style={{ marginRight: "8px" }}>{icons[entry.dataKey] || ""}</span>
              {`${entry.dataKey}: ${entry.value.toFixed(1)}%`}
            </p>
          )
        })}
      </div>
    )
  }
  return null
})

const NewStudentReport = ({ visible, onClose, student, onBack }) => {
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState("summary")
  const [practiceSubTab, setPracticeSubTab] = useState("overview")
  const [currentDate, setCurrentDate] = useState(new Date())
  const [componentError, setComponentError] = useState(null)

  // FIXED: Add activity data selectors
  const activityData = useSelector(selectActivityData)
  const activityLoading = useSelector(selectActivityLoading)
  const activityError = useSelector(selectActivityError)

  // Redux selectors (keeping all the same)
  const { formattedData: activityDataFormatted, setCurrentDate: setActivityCurrentDate } = useFormattedActivityData()

  const practiceScores = useSelector(selectPracticeScores)
  const practiceScoresLoading = useSelector(selectPracticeScoresLoading)
  const practiceScoresError = useSelector(selectPracticeScoresError)
  const studentDataSent = useSelector(selectStudentDataSent)
  const studentDataPending = useSelector(selectStudentDataPending)
  const studentDataInProgress = useSelector(selectStudentDataInProgress)
  const studentDataDroppedOff = useSelector(selectStudentDataDroppedOff)
  const studentDataCompleted = useSelector(selectStudentDataCompleted)
  const studentDataLoading = useSelector(selectStudentDataLoading)

  const assessmentScores = useSelector(selectAssessmentScores)
  const assessmentScoresLoading = useSelector(selectAssessmentScoresLoading)
  const assessmentScoresError = useSelector(selectAssessmentScoresError)
  const scoreInfo = useSelector(selectScoreInfo)
  const scoreInfoLoading = useSelector(selectScoreInfoLoading)
  const scoreInfoError = useSelector(selectScoreInfoError)

  const communicationAssessmentCount = useSelector(selectCommunicationAssessmentCount)
  const aptitudeAssessmentCount = useSelector(selectAptitudeAssessmentCount)
  const technicalAssessmentCount = useSelector(selectTechnicalAssessmentCount)
  const completedPracticeAssessmentsLoading = useSelector(selectCompletedPracticeAssessmentsLoading)
  const totalPracticeHours = useSelector(selectTotalPracticeHours)
  const formattedTotalPracticeHours = useSelector(selectFormattedTotalPracticeHours)
  const studentAssessments = useSelector(selectStudentAssessments)
  const studentAssessmentsLoading = useSelector(selectStudentAssessmentsLoading)
  const studentAssessmentsError = useSelector(selectStudentAssessmentsError)

  const lastAssessment = useSelector(selectLastAssessment)
  const lastAssessmentLoading = useSelector(selectLastAssessmentLoading)
  const lastAssessmentError = useSelector(selectLastAssessmentError)
  const lastPractice = useSelector(selectLastPractice)
  const lastPracticeLoading = useSelector(selectLastPracticeLoading)
  const lastPracticeError = useSelector(selectLastPracticeError)
  const formattedLastAssessment = useSelector(selectFormattedLastAssessment)
  const formattedLastPractice = useSelector(selectFormattedLastPractice)

  const cefrLevel = useSelector(selectCEFRLevel)
  const cefrLevelLoading = useSelector(selectCEFRLevelLoading)
  const cefrLevelError = useSelector(selectCEFRLevelError)
  const cefrScore = useSelector(selectCEFRScore)
  const formattedCEFRData = useSelector(selectFormattedCEFRData)
  const [studentProgressData, setStudentProgressData] = useState({
    communication: {
      progressMarks: null,
      assessmentsCount: 0,
      message: "No communication assessments found",
      isDiagnosisPending: true,
      assessment_cefr: "-"
    },
    aptitude: {
      progressMarks: null,
      assessmentLevelOfStudent: null,
      assessmentsCount: 0,
      message: "Aptitude diagnosis pending - please complete initial assessment",
      isDiagnosisPending: true
    }
  }) 

  // Helper functions (keeping the same logic)
  const getRelativeDate = (dateString, dynamicData) => {
    if (dynamicData?.formattedText) {
      return dynamicData.formattedText
    }
    if (!dateString) return "Never"
    return moment(dateString).fromNow()
  }

  const getCurrentMonthRange = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const startDate = new Date(year, month, 1).toISOString()
    const endDate = new Date(year, month + 1, 0, 23, 59, 59).toISOString()
    return { startDate, endDate }
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate)
    newDate.setMonth(currentDate.getMonth() + direction)
    setCurrentDate(newDate)
    setActivityCurrentDate(newDate)
  }

  const getMonthName = (date) => {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
  }

  const ActivityDayCell = ({ day, isActive, hasAchievement, cefr }) => (
    <ActivityDay active={isActive && day} title={day ? `Day ${day}${cefr ? ` - ${cefr}` : ""}` : ""}>
      {day ? (hasAchievement && isActive ? "🏆" : day) : ""}
    </ActivityDay>
  )

  // All the data processing functions (keeping the same logic)
  const generateChartData = () => {
    const practiceData = []
    const assessmentData = []

    if (practiceScores && practiceScores.length > 0) {
      let lastProgress = 0
      let lastReadingEMA = 0
      let lastListeningEMA = 0
      let lastSpeakingEMA = 0

      practiceData.push({
        session: 0,
        reading_score: 0,
        listening_score: 0,
        speaking_score: 0,
        current_session_score: 0,
        combined_progress: 0,
        type: "practice",
      })

      practiceScores.forEach((scoreData, index) => {
        const scores = {
          reading: scoreData.reading || 0,
          listening: scoreData.listening || 0,
          speaking: scoreData.speaking || 0,
        }

        const currentSessionScore = (scores.reading + scores.listening + scores.speaking) / 3
        const combinedProgress = 0.7 * lastProgress + 0.3 * currentSessionScore
        const readingEMA = 0.7 * lastReadingEMA + 0.3 * scores.reading
        const listeningEMA = 0.7 * lastListeningEMA + 0.3 * scores.listening
        const speakingEMA = 0.7 * lastSpeakingEMA + 0.3 * scores.speaking

        practiceData.push({
          session: index + 1,
          reading_score: readingEMA,
          listening_score: listeningEMA,
          speaking_score: speakingEMA,
          current_session_score: currentSessionScore,
          combined_progress: combinedProgress,
          type: "practice",
        })

        lastProgress = combinedProgress
        lastReadingEMA = readingEMA
        lastListeningEMA = listeningEMA
        lastSpeakingEMA = speakingEMA
      })
    }

    if (assessmentScores && assessmentScores.length > 0) {
      let lastCommunicationProgress = 0
      const sortedAssessmentScores = [...assessmentScores].sort(
        (a, b) => new Date(a.submittedAt) - new Date(b.submittedAt),
      )

      assessmentData.push({
        session: 0,
        communication_progress: 0,
        type: "assessment",
      })

      sortedAssessmentScores.forEach((scoreData, index) => {
        const reading = Math.min(scoreData.reading || 0, 100)
        const listening = Math.min(scoreData.listening || 0, 100)
        const speaking = Math.min(scoreData.speaking || 0, 100)

        const currentCommunicationScore = (reading + listening + speaking) / 3
        const communicationProgress = 0.7 * lastCommunicationProgress + 0.3 * currentCommunicationScore

        assessmentData.push({
          session: index + 1,
          communication_progress: communicationProgress,
          type: "assessment",
          submittedAt: scoreData.submittedAt,
        })

        lastCommunicationProgress = communicationProgress
      })
    }

    return { practiceData, assessmentData }
  }

  const prepareChartData = () => {
    const { practiceData, assessmentData } = generateChartData()

    const maxPracticeSessions = practiceData.length > 0 ? Math.max(...practiceData.map((p) => p.session)) : 0
    const maxAssessmentSessions = assessmentData.length > 0 ? Math.max(...assessmentData.map((a) => a.session)) : 0
    const maxSessions = Math.max(maxPracticeSessions, maxAssessmentSessions)

    const timelineData = []

    for (let sessionNum = 0; sessionNum <= maxSessions; sessionNum++) {
      const practiceSession = practiceData.find((p) => p.session === sessionNum)
      const assessmentSession = assessmentData.find((a) => a.session === sessionNum)

      timelineData.push({
        session: sessionNum,
        "Practice Progress": practiceSession ? practiceSession.combined_progress : null,
        Reading: practiceSession ? practiceSession.reading_score : null,
        Listening: practiceSession ? practiceSession.listening_score : null,
        Speaking: practiceSession ? practiceSession.speaking_score : null,
        "Communication Progress": assessmentSession ? assessmentSession.communication_progress : null,
        sessionType: practiceSession ? "Practice" : assessmentSession ? "Assessment" : "Mixed",
        originalPracticeSession: practiceSession ? practiceSession.session : null,
        originalAssessmentSession: assessmentSession ? assessmentSession.session : null,
        submittedAt: assessmentSession ? assessmentSession.submittedAt : null,
      })
    }

    return timelineData
  }

  const preparePracticeOnlyChartData = () => {
    const { practiceData } = generateChartData()

    return practiceData.map((session) => ({
      session: session.session,
      Reading: session.reading_score,
      Listening: session.listening_score,
      Speaking: session.speaking_score,
      "Practice Progress": session.combined_progress,
      sessionType: "Practice",
    }))
  }

  const calculateAssessmentMetrics = () => {
    if (!studentAssessments || studentAssessments.length === 0) {
      return {
        attemptRate: 0,
        totalCompleted: 0,
        totalSent: 0,
        communicationCompleted: 0,
        technicalCompleted: 0,
        cognitiveCompleted: 0,
        assessmentHistory: [],
      }
    }

    const filteredAssessments = studentAssessments.filter((assessment) => assessment.type !== "Behavior")

    const totalSent = filteredAssessments.length
    const completedAssessments = filteredAssessments.filter((assessment) => assessment.submitted === true)
    const totalCompleted = completedAssessments.length
    const attemptRate = totalSent > 0 ? Math.round((totalCompleted / totalSent) * 100) : 0

    const communicationCompleted = completedAssessments.filter((a) => a.type === "Communication").length
    const technicalCompleted = completedAssessments.filter((a) => a.type === "Technical").length
    const cognitiveCompleted = completedAssessments.filter((a) => a.type === "Cognitive").length

    const assessmentHistory = filteredAssessments.map((assessment) => {
      let status, statusColor, statusText
      if (assessment.submitted === true) {
        status = "completed"
        statusColor = "success"
        statusText = "Completed"
      } else {
        status = "not_attempted"
        statusColor = "default"
        statusText = "Not Attempted"
      }

      return {
        ...assessment,
        status,
        statusColor,
        statusText,
        assessmentAssignedId: assessment.assessment_assigned_id,
        // startTime/endTime are IST-wall-clock-as-UTC — render in UTC to match the true date.
        sentDate: new Date(assessment.startTime).toLocaleDateString('en-US', { timeZone: 'UTC' }),
        endDate: new Date(assessment.endTime).toLocaleDateString('en-US', { timeZone: 'UTC' }),
        assessmentName: assessment.title,
        assessmentType: assessment.type,
      }
    })

    assessmentHistory.sort((a, b) => new Date(b.startTime) - new Date(a.startTime))

    return {
      attemptRate,
      totalCompleted,
      totalSent,
      communicationCompleted,
      technicalCompleted,
      cognitiveCompleted,
      assessmentHistory,
    }
  }

  const getStudentAssessmentHistory = () => {
    const metrics = calculateAssessmentMetrics()
    return metrics.assessmentHistory
  }

  const calculateReadinessScore = () => {
    const { assessmentData } = generateChartData()
    let lastAssessmentEMA = 0

    if (assessmentData && assessmentData.length > 1) {
      const lastSession = assessmentData[assessmentData.length - 1]
      lastAssessmentEMA = lastSession.communication_progress || 0
    }

    const initialCEFR = scoreInfo?.firstCEFR || null

    if (!initialCEFR || lastAssessmentEMA === 0) {
      return { score: 0, isReady: false, reason: "Missing data" }
    }

    let isReady = false
    let reason = ""

    switch (initialCEFR.toUpperCase()) {
      case "A1":
        isReady = lastAssessmentEMA >= 31 && lastAssessmentEMA <= 100
        reason = `A1 level requires 31-100%, current: ${lastAssessmentEMA.toFixed(1)}%`
        break
      case "A2":
        isReady = lastAssessmentEMA >= 85 && lastAssessmentEMA <= 100
        reason = `A2 level requires 85-100%, current: ${lastAssessmentEMA.toFixed(1)}%`
        break
      case "B1":
        isReady = lastAssessmentEMA >= 71 && lastAssessmentEMA <= 100
        reason = `B1 level requires 71-100%, current: ${lastAssessmentEMA.toFixed(1)}%`
        break
      case "B2":
        isReady = lastAssessmentEMA >= 85 && lastAssessmentEMA <= 100
        reason = `B2 level requires 85-100%, current: ${lastAssessmentEMA.toFixed(1)}%`
        break
      case "C1":
        isReady = lastAssessmentEMA >= 95 && lastAssessmentEMA <= 100
        reason = `C1 level requires 95-100%, current: ${lastAssessmentEMA.toFixed(1)}%`
        break
      case "C2":
        isReady = lastAssessmentEMA >= 95 && lastAssessmentEMA <= 100
        reason = `C2 level requires 95-100%, current: ${lastAssessmentEMA.toFixed(1)}%`
        break
      default:
        return { score: 0, isReady: false, reason: `Unknown CEFR level: ${initialCEFR}` }
    }

    const score = Math.round(lastAssessmentEMA)
    return { score, isReady, reason }
  }

  const handleViewDetails = (assessment) => {
    // Implementation for viewing assessment details
  }

  // FIXED: Add activity data retry function
  const handleActivityRetry = async (date, startDate, endDate) => {
    try {
      if (student?.email) {
        const dateRangeToUse = startDate && endDate 
          ? { startDate, endDate }
          : getCurrentMonthRange(date || currentDate)
        
        await dispatch(fetchActivityMap(student.email, true, dateRangeToUse.startDate, dateRangeToUse.endDate))
      }
    } catch (error) {
      console.error('Error retrying activity data:', error)
    }
  }

  // Render Activity Tracker
  const renderActivityTracker = () => (
    <ActivityCard bordered={false} title="Activity Tracker">
      <MonthNavigation>
        <div className="nav-button" onClick={() => navigateMonth(-1)}>
          <LeftOutlined />
        </div>
        <div className="month-label">{getMonthName(currentDate)}</div>
        <div className="nav-button" onClick={() => navigateMonth(1)}>
          <RightOutlined />
        </div>
      </MonthNavigation>

      {activityLoading ? (
        <LoadingWrapper>
          <Spin size="small" />
          <div className="loading-text">Loading activity data...</div>
        </LoadingWrapper>
      ) : (
        <>
          <WeekdaysGrid>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
              <WeekdayLabel key={day}>{day}</WeekdayLabel>
            ))}
          </WeekdaysGrid>
          <ActivityGrid>
            {activityData.map((cell, idx) => (
              <ActivityDayCell
                key={idx}
                day={cell.day}
                isActive={cell.isActive}
                hasAchievement={cell.hasAchievement}
                cefr={cell.cefr}
              />
            ))}
          </ActivityGrid>
        </>
      )}

      <LegendRow>
        <div className="legend-item">
          <div className="legend-dot"></div>
          <span>Active days</span>
        </div>
        <div className="legend-item">
          <TrophyOutlined className="legend-trophy" />
          <span>Achievement unlocked</span>
        </div>
      </LegendRow>
    </ActivityCard>
  )

  const readinessData = calculateReadinessScore()

  console.log("student raw", student);

  const studentData = {
    name: student?.studentName,
    avatar: student?.student_img_url,
    lastAssessment: formattedLastAssessment?.lastDate || student?.completedDateTime || student?.lastAttemptDate,
    lastPractice: formattedLastPractice?.lastDate || student?.lastPracticeDate,
    readinessScore: readinessData.score,
    isReady: readinessData.isReady,
    readinessReason: readinessData.reason,
    communication: {
      score: studentProgressData?.communication?.progressMarks || 0,
      level: studentProgressData?.communication?.assessment_cefr || "-",
      improvement: studentProgressData?.communication?.improvement || 0,
    },
    aptitude: { score: studentProgressData?.aptitude?.progressMarks || 0, level: studentProgressData?.aptitude?.assessmentLevelOfStudent || "-", improvement: studentProgressData?.aptitude?.improvement || 0 },
 //   technical: { score: 50, level: "Beginner", improvement: 30 },
  }

  console.log("Student Data:", studentData)

  const chartDataMemo = useMemo(() => generateChartData(), [practiceScores, assessmentScores])
  const assessmentMetricsMemo = useMemo(() => calculateAssessmentMetrics(), [studentAssessments])

  const chartData = chartDataMemo
  const assessmentMetrics = assessmentMetricsMemo
  const studentAssessmentHistory = getStudentAssessmentHistory()

  // Effects (keeping the same)
  useEffect(() => {
    if (student?.studentNumber) {
      fetchStudentProgressData()
    }
  }, [student?.studentNumber])

  const fetchStudentProgressData = async () => {
    try {
      const response = await dispatch(fetchStudentProgress(student?.studentNumber))
      setStudentProgressData(response?.data?.data)
    } catch (error) {
      console.error("Error fetching student progress:", error)
    }
  }
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (activityError) {
          message.error(`Failed to load activity data: ${activityError}`)
        }
        if (student?.email) {
          const { startDate, endDate } = getCurrentMonthRange(currentDate)
          dispatch(setActivityDateRange(startDate, endDate))
          await dispatch(fetchPracticeScores(student.email))
          await dispatch(fetchActivityMap(student.email, true, startDate, endDate))
        }
      } catch (error) {
        console.error("Error fetching data:", error)
        setComponentError("Failed to load student data")
      }
    }

    fetchData()
  }, [dispatch, activityError, currentDate, student?.email])

  useEffect(() => {
    if (visible && student?.studentNumber) {
      dispatch(fetchCompletedPracticeAssessments(student.studentNumber, true))
      dispatch(fetchStudentAssessments(student.studentNumber, false))
    }
  }, [dispatch, visible, student?.studentNumber])

  useEffect(() => {
    if (visible && student?.email) {
      dispatch(fetchLastAssessment(student.email))
      dispatch(fetchLastPractice(student.email))
    }
  }, [dispatch, visible, student?.email])

  useEffect(() => {
    if (visible && student?.email) {
      dispatch(fetchCEFRLevel(student.email))
    }
  }, [dispatch, visible, student?.email])

  useEffect(() => {
    if (visible && student?.email) {
      dispatch(fetchAssessmentScores(student.email))
    }
  }, [dispatch, visible, student?.email])

  useEffect(() => {
    if (visible && student?.email) {
      dispatch(fetchScoreInfo(student.email))
    }
  }, [dispatch, visible, student?.email])

  if (componentError) {
    return (
      <StyledDrawer
        title="Error"
        placement="right"
        onClose={onClose}
        visible={visible}
        width={Math.min(750, window.innerWidth * 0.9)}
      >
        <div style={{ padding: "24px", textAlign: "center" }}>
          <Alert message="Error Loading Student Report" description={componentError} type="error" showIcon />
          <Button
            type="primary"
            onClick={() => {
              setComponentError(null)
              window.location.reload()
            }}
            style={{ marginTop: "16px" }}
          >
            Retry
          </Button>
        </div>
      </StyledDrawer>
    )
  }

  return (
    <StyledDrawer
      title=""
      placement="right"
      closable={true}
      onClose={onClose}
      visible={visible}
      width={Math.min(750, window.innerWidth * 0.9)}
      bodyStyle={{ padding: 0 }}
      headerStyle={{ display: "none" }}
      closeIcon={<CloseOutlined style={{ color: "white", fontSize: "16px" }} />}
    >
      <HeaderSection>
        {onBack && (
          <BackButton icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back
          </BackButton>
        )}
        <StudentInfo>
          <Avatar size={64} src={studentData.avatar} style={{ border: "2px solid #f0f0f0" }}>
            {studentData.name?.charAt(0)}
          </Avatar>
          <Title level={3} style={{ color: "#262626", margin: 0, fontWeight: 600 }}>
            {studentData.name}
          </Title>
          <DateInfo>
            <div>
              <ClockCircleOutlined style={{ marginRight: "8px" }} />
              Last assessment:{" "}
              {lastAssessmentLoading
                ? "Loading..."
                : lastAssessmentError
                  ? "Error loading"
                  : getRelativeDate(studentData.lastAssessment, formattedLastAssessment)}
            </div>
            <div>
              <TrophyOutlined style={{ marginRight: "8px" }} />
              Last practice:{" "}
              {lastPracticeLoading
                ? "Loading..."
                : lastPracticeError
                  ? "Error loading"
                  : getRelativeDate(studentData.lastPractice, formattedLastPractice)}
            </div>
          </DateInfo>
        </StudentInfo>
      </HeaderSection>

      <StyledTabs activeKey={activeTab} onChange={setActiveTab} size="small">
        <TabPane tab="Summary" key="summary">
          <SummaryTab
            studentData={studentData}
            mockProgressData={mockProgressData}
            suggestedRoles={suggestedRoles}
            cefrLevelLoading={cefrLevelLoading}
            cefrLevelError={cefrLevelError}
            chartData={chartData}
            prepareChartData={prepareChartData}
            CustomTooltip={CustomTooltip}
            practiceScoresLoading={practiceScoresLoading}
            practiceScoresError={practiceScoresError}
            assessmentScoresLoading={assessmentScoresLoading}
            assessmentScoresError={assessmentScoresError}
            student={student}
          />
        </TabPane>
        <TabPane tab="Current Assessment" key="currentAssessment">
          <StudentReport
            student={student}
         
          />
        </TabPane>
        <TabPane tab="Assessment" key="assessment">
          <AssessmentTab
            assessmentMetrics={assessmentMetrics}
            studentAssessmentHistory={studentAssessmentHistory}
            studentDataLoading={studentDataLoading}
            handleViewDetails={handleViewDetails}
            student={student}
          />
        </TabPane>
        <TabPane tab="Practice" key="practice">
          <PracticeTab
            practiceSubTab={practiceSubTab}
            setPracticeSubTab={setPracticeSubTab}
            renderActivityTracker={renderActivityTracker}
            practiceScoresLoading={practiceScoresLoading}
            practiceScoresError={practiceScoresError}
            chartData={chartData}
            prepareChartData={prepareChartData}
            CustomTooltip={CustomTooltip}
            communicationAssessmentCount={communicationAssessmentCount}
            aptitudeAssessmentCount={aptitudeAssessmentCount}
            technicalAssessmentCount={technicalAssessmentCount}
            completedPracticeAssessmentsLoading={completedPracticeAssessmentsLoading}
            totalPracticeHours={totalPracticeHours}
            formattedTotalPracticeHours={formattedTotalPracticeHours}
            // FIXED: Pass activity data props
            student = {student}
            activityData={activityData}
            activityLoading={activityLoading}
            activityError={activityError}
            onActivityRetry={handleActivityRetry}
          />
        </TabPane>
      </StyledTabs>
    </StyledDrawer>
  )
}

export default NewStudentReport
